// Phase 3 — Preview-only OptiGuard matrix adjustment helper.
import { useMemo } from 'react';
import {
  computeOptiGuardMatrixAdjustment,
  FALLBACK_PRICING_MATRIX,
  DEFAULT_MATRIX_KEY,
  type PricingMatrixRow,
  type ShopPricingConfig,
} from '@/lib/parts-financial/calculator';
import { usePartsPricingEvents } from './usePartsPricingEvents';

export interface OptiGuardSuggestion {
  adjustedMatrix: PricingMatrixRow[] | null;
  historicalGpPct: number | null;
  sampleCount: number;
  belowMinSamples: boolean;
}

interface Args {
  shopId: string | undefined;
  category: string | null;
  config: ShopPricingConfig;
  targetGpPct: number | null | undefined;
  minSampleCount: number;
  maxStepPct: number;
}

export function useOptiGuardSuggestion(args: Args): OptiGuardSuggestion {
  // CSI-P2: aggregateOnly=true excludes lifecycle audit events
  // (event_type IS NOT NULL) so historical GP% averaging weights each
  // reapply once, not twice. Pre-CSI-P2 the OptiGuard recommendation
  // engine silently received 2× contribution from manual_override_removed
  // events because the CSI-P1 dual-event design writes both a trigger
  // event AND a lifecycle event for the same UPDATE.
  const eventsQ = usePartsPricingEvents({
    shopId: args.shopId,
    category: args.category,
    excludeCanned: true,
    aggregateOnly: true,
    limit: 500,
  });

  return useMemo(() => {
    const events = eventsQ.data ?? [];
    const valid = events.filter(
      (e) => e.unit_cost != null && e.actual_sell != null && Number(e.actual_sell) > 0,
    );
    const sampleCount = valid.length;

    if (args.targetGpPct == null) {
      return { adjustedMatrix: null, historicalGpPct: null, sampleCount, belowMinSamples: sampleCount < args.minSampleCount };
    }
    if (sampleCount === 0) {
      return { adjustedMatrix: null, historicalGpPct: null, sampleCount, belowMinSamples: true };
    }

    // Avg historical GP%
    let totalGp = 0;
    for (const e of valid) {
      const cost = Number(e.unit_cost ?? 0);
      const sell = Number(e.actual_sell ?? 0);
      if (sell <= 0) continue;
      totalGp += ((sell - cost) / sell) * 100;
    }
    const historicalGp = totalGp / sampleCount;

    const key = args.category ?? DEFAULT_MATRIX_KEY;
    const currentMatrix =
      args.config.matrixByCategory.get(key) ??
      args.config.matrixByCategory.get(DEFAULT_MATRIX_KEY) ??
      FALLBACK_PRICING_MATRIX;

    const adjusted = computeOptiGuardMatrixAdjustment(
      historicalGp,
      args.targetGpPct,
      currentMatrix,
      { maxStepPct: args.maxStepPct, minSamples: args.minSampleCount, sampleCount },
    );

    return {
      adjustedMatrix: adjusted,
      historicalGpPct: Number(historicalGp.toFixed(2)),
      sampleCount,
      belowMinSamples: sampleCount < args.minSampleCount,
    };
  }, [eventsQ.data, args.targetGpPct, args.category, args.config, args.minSampleCount, args.maxStepPct]);
}
